import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { clearbitLogoFromWebsite } from '../../utils/clearbitLogo';
import { isSystemAdmin } from './aivisRoles';
import { chartSeries, fitColors, tokens } from './marketingTokens';
import './ai-vis.css';

const PAGE_SIZE = 20;

const LINE_COLORS = chartSeries;

const COL_TOOLTIPS = {
  url: 'The specific URL from this domain that was cited in AI responses.',
  classifier:
    'Domain-level classification — Corporate, UGC (Reddit/forums), Editorial, Ecommerce, and more.',
  url_type:
    'Page-level classification based on URL and title — Homepage, Product Roundup, Review, Ecommerce, Video, Reddit, and more.',
  client_mentioned:
    "Whether the client was mentioned on this URL's page, verified by crawling the page content.",
  times_cited:
    'Total number of unique AI chat responses that cited this URL across all runs for this campaign.',
  relevance_check:
    'Gemini PR fit score — Good Fit means this article is a strong pitching opportunity.',
  n8n_sent:
    'Whether this URL has been sent to the n8n prospecting workflow for outreach.',
  last_seen:
    'How long ago this URL was last seen as a source in a chat response.',
};

const FIT_COLORS = fitColors;

function typeClass(classifier) {
  const map = {
    competitor: 'aivis-dash-type--competitor',
    reddit: 'aivis-dash-type--ugc',
    product_roundup: 'aivis-dash-type--editorial',
    review: 'aivis-dash-type--review',
    ecommerce: 'aivis-dash-type--commerce',
    review_site: 'aivis-dash-type--reviewsite',
    client: 'aivis-dash-type--you',
    homepage: 'aivis-dash-type--corp',
    video: 'aivis-dash-type--video',
    other: 'aivis-dash-type--other',
  };
  return `aivis-dash-type ${map[classifier] || 'aivis-dash-type--other'}`;
}

function formatShortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 2) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch {
    return '—';
  }
}

/** Full URL without scheme (domain + path), truncated — used in table */
function urlFullDisplay(url, maxLen = 70) {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const full = u.hostname + u.pathname + (u.search || '');
    return full.length > maxLen ? `${full.slice(0, maxLen)}…` : full;
  } catch {
    return url.length > maxLen ? `${url.slice(0, maxLen)}…` : url;
  }
}

/** Path only — kept short for the chart legend */
function urlShortPath(url) {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    const p = u.pathname + (u.search || '');
    return p.length > 45 ? `${p.slice(0, 45)}…` : p || '/';
  } catch {
    return url.length > 45 ? `${url.slice(0, 45)}…` : url;
  }
}

/* ─── Tooltip portal (same pattern as DomainsPage) ─── */
const TOOLTIP_W = 230;
const TOOLTIP_MARGIN = 12;

function TooltipPortal({ text, anchorRef, visible }) {
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const anchorCX = r.left + r.width / 2;
    let left = anchorCX - TOOLTIP_W / 2;
    left = Math.max(TOOLTIP_MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - TOOLTIP_MARGIN));
    const arrowLeft = Math.round(Math.max(14, Math.min(anchorCX - left, TOOLTIP_W - 14)));
    setStyle({
      left,
      top: r.top - 10,
      transform: 'translateY(-100%)',
      '--arrow-left': `${arrowLeft}px`,
    });
  }, [visible, anchorRef]);

  if (!visible || !text) return null;
  return createPortal(
    <div className="aivis-domains-tooltip-popup" style={style} role="tooltip">
      {text}
    </div>,
    document.body
  );
}

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  return (
    <span
      ref={ref}
      className="aivis-domains-th-info"
      aria-label={text}
      role="img"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      tabIndex={0}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 7v5M8 5.25V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <TooltipPortal text={text} anchorRef={ref} visible={visible} />
    </span>
  );
}

function SortIcon({ active, dir }) {
  if (active) {
    return (
      <svg
        width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden
        className="aivis-domains-sort-icon aivis-domains-sort-icon--active"
      >
        {dir === 'asc'
          ? <path d="M6 2L10.5 9H1.5L6 2Z" fill="currentColor" />
          : <path d="M6 10L1.5 3H10.5L6 10Z" fill="currentColor" />}
      </svg>
    );
  }
  return (
    <svg
      width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden
      className="aivis-domains-sort-icon"
    >
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity="0.4" />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

function ColHeader({ label, sortKey, currentSort, currentDir, onSort, tooltip, numeric }) {
  const active = currentSort === sortKey;
  return (
    <th
      scope="col"
      className={`${numeric ? 'aivis-domains-th-num' : ''} ${sortKey ? 'aivis-domains-th-sortable' : ''}`}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <div className="aivis-domains-th-inner">
        <span className="aivis-domains-th-label">{label}</span>
        {sortKey && <SortIcon active={active} dir={currentDir} />}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
    </th>
  );
}

function PaginationControls({ page, totalPages, totalItems, onPage }) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('…');
    const lo = Math.max(2, page - 1);
    const hi = Math.min(totalPages - 1, page + 1);
    for (let i = lo; i <= hi; i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }
  return (
    <div className="aivis-domains-pagination">
      <div className="aivis-domains-pg-controls">
        <button type="button" className="aivis-domains-pg-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">‹</button>
        {pages.map((p, idx) =>
          p === '…'
            ? <span key={`e${idx}`} className="aivis-domains-pg-ellipsis">…</span>
            : <button key={p} type="button" className={`aivis-domains-pg-btn ${p === page ? 'aivis-domains-pg-btn--active' : ''}`} onClick={() => onPage(p)}>{p}</button>
        )}
        <button type="button" className="aivis-domains-pg-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next page">›</button>
      </div>
      <span className="aivis-domains-pg-count">{totalItems.toLocaleString()} item{totalItems === 1 ? '' : 's'}</span>
    </div>
  );
}

function SearchIcon() {
  return (
    <span className="aivis-prompts-search-icon" aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

export default function DomainDetailPage() {
  const { domain } = useParams();
  const { selectedCampaignId, campaigns, user } = useOutletContext();
  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));
  const clientName = selected?.client_name || 'Client';

  // "Sent to n8n" column: system admins only — hidden from all client accounts.
  const showN8nColumn = isSystemAdmin(user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('times_cited');
  const [sortDir, setSortDir] = useState('desc');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    setSearch('');
    setPage(1);
    setData(null);
    setError('');
  }, [selectedCampaignId, domain]);

  useEffect(() => {
    if (!selectedCampaignId || !domain) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    api
      .get(`/aivis/campaigns/${selectedCampaignId}/domains/${encodeURIComponent(domain)}/detail/`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Could not load domain details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId, domain]);

  function handleSearchChange(e) {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (!selectedCampaignId || !domain) return;
      setLoading(true);
      const params = new URLSearchParams();
      if (val.trim()) params.set('search', val.trim());
      api
        .get(`/aivis/campaigns/${selectedCampaignId}/domains/${encodeURIComponent(domain)}/detail/?${params}`)
        .then((res) => setData(res.data))
        .catch((e) => setError(e.response?.data?.detail || 'Could not load domain details.'))
        .finally(() => setLoading(false));
    }, 320);
  }

  function handleSort(key) {
    if (sortKey === key) { setSortDir((d) => (d === 'desc' ? 'asc' : 'desc')); }
    else { setSortKey(key); setSortDir('desc'); }
    setPage(1);
  }

  const sortedUrls = useMemo(() => {
    const arr = [...(data?.urls || [])];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 : 1;
      if (as > bs) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data?.urls, sortKey, sortDir]);

  const totalUrls = sortedUrls.length;
  const totalPages = Math.max(1, Math.ceil(totalUrls / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageUrls = sortedUrls.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const lineOrder = useMemo(() => data?.series_url_order || [], [data?.series_url_order]);
  const chartData = useMemo(
    () => (data?.series || []).map((row) => ({ date: row.date, label: formatShortDate(row.date), ...(row.metrics || {}) })),
    [data?.series]
  );

  // Shorten URL keys for legend display
  const lineLabels = useMemo(() => {
    const obj = {};
    lineOrder.forEach((url) => { obj[url] = urlShortPath(url); });
    return obj;
  }, [lineOrder]);

  const yMax = useMemo(() => {
    const vals = chartData.flatMap((d) => lineOrder.map((k) => (typeof d[k] === 'number' ? d[k] : 0)));
    const peak = vals.length ? Math.max(...vals) : 0;
    return Math.min(100, Math.ceil((peak + 5) / 5) * 5);
  }, [chartData, lineOrder]);

  const renderTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="aivis-dash-tooltip">
        <div className="aivis-dash-tooltip-title">{label}</div>
        {payload.filter((p) => p.dataKey !== 'label' && p.dataKey !== 'date').map((p) => (
          <div key={String(p.dataKey)} className="aivis-dash-tooltip-row">
            <span className="aivis-dash-tooltip-dot" style={{ background: p.color }} />
            <span>{lineLabels[p.dataKey] || p.name}</span>
            <strong>{typeof p.value === 'number' ? `${p.value}%` : p.value}</strong>
          </div>
        ))}
      </div>
    );
  }, [lineLabels]);

  const stats = data?.domain_stats;
  const logoSrc = clearbitLogoFromWebsite(domain);

  return (
    <div className="aivis-domains-page">
      {/* Breadcrumb */}
      <nav className="aivis-domain-detail-breadcrumb" aria-label="Breadcrumb">
        <Link to="/ai-vis/domains" className="aivis-domain-detail-bc-link">Cited Domains</Link>
        <span className="aivis-domain-detail-bc-sep" aria-hidden>›</span>
        <span className="aivis-domain-detail-bc-current">{domain}</span>
      </nav>

      {/* Header */}
      <header className="aivis-domain-detail-header">
        <div className="aivis-domain-detail-identity">
          {logoSrc && (
            <img
              src={logoSrc}
              alt=""
              width={36}
              height={36}
              className="aivis-domain-detail-logo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <h1 className="aivis-domain-detail-title">{domain}</h1>
          {stats && <span className={typeClass(stats.classifier)}>{stats.type_label}</span>}
        </div>
        {stats && (
          <div className="aivis-domain-detail-stats">
            <div className="aivis-domain-detail-stat">
              <span className="aivis-domain-detail-stat-label">Retrieved</span>
              <span className="aivis-domain-detail-stat-value">{stats.retrieved_pct}%</span>
            </div>
            <div className="aivis-domain-detail-stat-divider" aria-hidden />
            <div className="aivis-domain-detail-stat">
              <span className="aivis-domain-detail-stat-label">Citation rate</span>
              <span className="aivis-domain-detail-stat-value">{stats.retrieval_rate}</span>
            </div>
            <div className="aivis-domain-detail-stat-divider" aria-hidden />
            <div className="aivis-domain-detail-stat">
              <span className="aivis-domain-detail-stat-label">Citation rate</span>
              <span className="aivis-domain-detail-stat-value">{stats.citation_rate}</span>
            </div>
          </div>
        )}
      </header>

      {error ? <div className="aivis-auth-error">{error}</div> : null}

      {/* Section 1: Line chart */}
      <section className="aivis-dash-section" aria-label="URL retrieval over time">
        <div className="aivis-dash-card">
          <div className="aivis-dash-card-head">
            <h2 className="aivis-dash-card-title">Source Citations by URL</h2>
            <p className="aivis-dash-card-desc">
              % of chats where each top URL from this domain appeared as a source (by day).
            </p>
          </div>
          <div className="aivis-dash-chart-wrap aivis-dash-chart-wrap--tall">
            {loading && !chartData.length ? (
              <p className="aivis-dash-empty">Loading…</p>
            ) : chartData.length && lineOrder.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.graphite200} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={tokens.graphite500} />
                  <YAxis domain={[0, yMax]} tick={{ fontSize: 11 }} stroke={tokens.graphite500} width={36} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={renderTooltip} />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value) => lineLabels[value] || value}
                  />
                  {lineOrder.map((key, i) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="aivis-dash-empty">
                {selectedCampaignId ? 'No URL data yet for this domain.' : 'Select a campaign.'}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Section 2: URLs table */}
      <section className="aivis-dash-section" aria-label="URLs table">
        <div className="aivis-dash-card">
          <div className="aivis-domains-table-toolbar">
            <div className="aivis-prompts-search-wrap">
              <SearchIcon />
              <input
                type="search"
                className="aivis-prompts-search"
                placeholder="Search URLs"
                value={search}
                onChange={handleSearchChange}
                aria-label="Search URLs"
              />
            </div>
          </div>

          <div className="aivis-domains-table-scroll">
            <table className="aivis-domains-table aivis-url-table" style={{ minWidth: showN8nColumn ? 980 : 860 }}>
              <colgroup>
                {/* # */}
                <col style={{ width: '4%' }} />
                {/* URL */}
                <col style={{ width: showN8nColumn ? '25%' : '30%' }} />
                {/* URL type */}
                <col style={{ width: showN8nColumn ? '10%' : '11%' }} />
                {/* Client Mentioned */}
                <col style={{ width: showN8nColumn ? '13%' : '15%' }} />
                {/* Times Cited */}
                <col style={{ width: '10%' }} />
                {/* Fit Score */}
                <col style={{ width: showN8nColumn ? '13%' : '14%' }} />
                {/* Last Seen */}
                <col style={{ width: showN8nColumn ? '13%' : '16%' }} />
                {/* Sent to N8N (only when visible) */}
                {showN8nColumn && <col style={{ width: '12%' }} />}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="aivis-domains-th-num">#</th>
                  <ColHeader label="URL" sortKey="url" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.url} />
                  <ColHeader label="URL type" sortKey="url_type_label" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.url_type} />
                  <ColHeader label="Client Mentioned" sortKey="client_mentioned" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.client_mentioned} />
                  <ColHeader label="Times Cited" sortKey="times_cited" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.times_cited} numeric />
                  <ColHeader label="Fit Score" sortKey="relevance_check" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.relevance_check} />
                  <ColHeader label="Last Seen" sortKey="last_seen" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.last_seen} />
                  {showN8nColumn && (
                    <ColHeader label="Sent to n8n" sortKey="n8n_sent" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} tooltip={COL_TOOLTIPS.n8n_sent} />
                  )}
                </tr>
              </thead>
              <tbody>
                {pageUrls.length === 0 ? (
                  <tr>
                    <td colSpan={showN8nColumn ? 9 : 8} className="aivis-domains-empty">
                      {loading ? 'Loading…' : search ? 'No URLs match your search.' : 'No URL data yet.'}
                    </td>
                  </tr>
                ) : (
                  pageUrls.map((row, idx) => (
                    <tr key={row.url} className="aivis-domains-tr">
                      <td className="aivis-domains-td-num aivis-domains-td-rank">
                        {(safePage - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="aivis-url-td-url">
                        <div className="aivis-url-cell">
                          {row.page_title && (
                            <span className="aivis-url-title" title={row.page_title}>{row.page_title}</span>
                          )}
                          <a
                            href={row.url.startsWith('http') ? row.url : `https://${row.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="aivis-url-href"
                            title={row.url}
                          >
                            {urlFullDisplay(row.url)}
                          </a>
                        </div>
                      </td>
                      <td>
                        <span className={typeClass(row.classifier)}>{row.url_type_label}</span>
                      </td>
                      <td>
                        <span className={`aivis-mentioned ${row.client_mentioned ? 'aivis-mentioned--yes' : 'aivis-mentioned--no'}`}>
                          {row.client_mentioned ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="aivis-domains-td-num">{row.times_cited}</td>
                      <td>
                        {row.relevance_check ? (
                          <span
                            className="aivis-sources-fit-badge"
                            style={{
                              background: (FIT_COLORS[row.relevance_check] || {}).bg || 'var(--aivis-bg)',
                              color: (FIT_COLORS[row.relevance_check] || {}).color || 'var(--aivis-text-muted)',
                            }}
                          >
                            {row.relevance_check}
                          </span>
                        ) : <span className="aivis-sources-fit-badge aivis-sources-fit-badge--none">—</span>}
                      </td>
                      <td className="aivis-url-td-updated" title={row.last_seen || ''}>
                        {relativeTime(row.last_seen)}
                      </td>
                      {showN8nColumn && (
                        <td>
                          {row.n8n_sent ? (
                            <span className="aivis-sources-sent-badge aivis-sources-sent-badge--yes">Sent</span>
                          ) : (
                            <span className="aivis-sources-sent-badge aivis-sources-sent-badge--no">Not sent</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalUrls > PAGE_SIZE ? (
            <PaginationControls page={safePage} totalPages={totalPages} totalItems={totalUrls} onPage={(p) => setPage(p)} />
          ) : totalUrls > 0 ? (
            <p className="aivis-domains-count">{totalUrls.toLocaleString()} URL{totalUrls === 1 ? '' : 's'}</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
